import {
	type RecordProxy,
	type ReaderLinkedField,
	type ReaderScalarField,
	type ReaderSelection,
} from 'relay-runtime';

import { getRelayFieldStorageKey } from './relayStorageKeys.ts';

// Bounded recursive walk of the query selection tree. This module keeps the
// Relay-specific in-place write mechanics out of writeQueryInterop so that
// interop routing remains easy to audit.
/* eslint-disable @atlassian/perf-linting/no-quadratic-complexity */
export function writeQueryFields(
	record: RecordProxy,
	fields: readonly ReaderSelection[],
	data: unknown,
	variables: Record<string, unknown>,
): boolean {
	if (!record || !isObjectRecord(data)) {
		return false;
	}

	// Relay commitUpdate is not transactional. Preflight the selection tree before
	// writing so list rewrites that require Relay normalization fall through to
	// commitPayload instead of committing partial in-place updates.
	if (!canWriteQueryFields(record, fields, data, variables)) {
		return false;
	}

	return writeQueryFieldsUnsafe(record, fields, data, variables);
}

function canWriteQueryFields(
	record: RecordProxy,
	fields: readonly ReaderSelection[],
	data: Record<string, unknown>,
	variables: Record<string, unknown>,
): boolean {
	for (const field of fields) {
		if (field.kind === 'Condition') {
			const conditionValue = variables[field.condition];
			const shouldInclude = field.passingValue ? conditionValue : !conditionValue;
			if (shouldInclude && !canWriteQueryFields(record, field.selections, data, variables)) {
				return false;
			}
			continue;
		}

		if (field.kind === 'InlineFragment') {
			if (!canWriteQueryFields(record, field.selections, data, variables)) {
				return false;
			}
			continue;
		}

		if (field.kind === 'LinkedField') {
			const linkedField = field as ReaderLinkedField;
			const responseKey = linkedField.alias ?? linkedField.name;
			if (!Object.prototype.hasOwnProperty.call(data, responseKey)) {
				continue;
			}

			const fieldData = data[responseKey];
			if (fieldData == null || !linkedField.selections) {
				continue;
			}

			const fieldKey = getRelayFieldStorageKey(linkedField, variables);
			if (linkedField.plural) {
				if (!Array.isArray(fieldData)) {
					return false;
				}

				const existingRecords = record.getLinkedRecords(fieldKey);
				if (!existingRecords || fieldData.length !== existingRecords.length) {
					return false;
				}

				for (let index = 0; index < fieldData.length; index++) {
					const item = fieldData[index];
					if (item == null) {
						continue;
					}
					if (!isObjectRecord(item)) {
						return false;
					}

					const existingRecord = existingRecords[index];
					if (!existingRecord || hasDifferentRecordId(existingRecord, item)) {
						return false;
					}

					if (!canWriteQueryFields(existingRecord, linkedField.selections, item, variables)) {
						return false;
					}
				}
				continue;
			}

			if (!isObjectRecord(fieldData)) {
				return false;
			}

			const linkedRecord = record.getLinkedRecord(fieldKey);
			if (!linkedRecord || hasDifferentRecordId(linkedRecord, fieldData)) {
				return false;
			}

			if (!canWriteQueryFields(linkedRecord, linkedField.selections, fieldData, variables)) {
				return false;
			}
			continue;
		}

		if (field.kind === 'ScalarField') {
			const scalarField = field as ReaderScalarField;
			const responseKey = scalarField.alias ?? scalarField.name;
			const scalarValue = data[responseKey];
			if (scalarValue !== undefined && !isRelaySetValue(scalarValue)) {
				return false;
			}
			continue;
		}
	}

	return true;
}

function writeQueryFieldsUnsafe(
	record: RecordProxy,
	fields: readonly ReaderSelection[],
	data: Record<string, unknown>,
	variables: Record<string, unknown>,
): boolean {
	let didWrite = false;
	for (const field of fields) {
		if (field.kind === 'Condition') {
			// Honour @include/@skip so conditional fields (e.g. userPreferences) get written.
			const conditionValue = variables[field.condition];
			const shouldInclude = field.passingValue ? conditionValue : !conditionValue;
			if (shouldInclude) {
				didWrite = writeQueryFieldsUnsafe(record, field.selections, data, variables) || didWrite;
			}
		} else if (field.kind === 'InlineFragment') {
			didWrite = writeQueryFieldsUnsafe(record, field.selections, data, variables) || didWrite;
		} else if (field.kind === 'LinkedField') {
			const linkedField = field as ReaderLinkedField;
			const responseKey = linkedField.alias ?? linkedField.name;
			const fieldData = data[responseKey];
			if (fieldData == null || !linkedField.selections) {
				continue;
			}

			const fieldKey = getRelayFieldStorageKey(linkedField, variables);

			if (linkedField.plural) {
				if (!Array.isArray(fieldData)) {
					continue;
				}
				// Read plural fields with getLinkedRecords; the singular getter throws on
				// plural data. Missing/changed list records are handled by the commitPayload
				// fallback so this in-place path only updates known records.
				const existingRecords = record.getLinkedRecords(fieldKey);
				if (!existingRecords) {
					continue;
				}
				for (let index = 0; index < fieldData.length; index++) {
					const item = fieldData[index];
					const existingRecord = existingRecords[index];
					if (existingRecord && isObjectRecord(item)) {
						didWrite =
							writeQueryFieldsUnsafe(existingRecord, linkedField.selections, item, variables) ||
							didWrite;
					}
				}
			} else if (isObjectRecord(fieldData)) {
				const linkedRecord = record.getLinkedRecord(fieldKey);

				if (linkedRecord) {
					didWrite =
						writeQueryFieldsUnsafe(linkedRecord, linkedField.selections, fieldData, variables) ||
						didWrite;
				}
			}
		} else if (field.kind === 'ScalarField') {
			const scalarField = field as ReaderScalarField;
			const responseKey = scalarField.alias ?? scalarField.name;
			const scalarValue = data[responseKey];
			if (scalarValue !== undefined && isRelaySetValue(scalarValue)) {
				record.setValue(scalarValue, getRelayFieldStorageKey(scalarField, variables));
				didWrite = true;
			}
		}
	}

	return didWrite;
}

function getRecordIdFromData(data: Record<string, unknown>): string | null {
	const id = data.id;

	return typeof id === 'string' || typeof id === 'number' ? String(id) : null;
}

function hasDifferentRecordId(record: RecordProxy, data: Record<string, unknown>): boolean {
	const id = getRecordIdFromData(data);

	return Boolean(id && record.getDataID() !== id);
}

function isObjectRecord(value: unknown): value is Record<string, unknown> {
	return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

type RelaySetValue = Parameters<RecordProxy['setValue']>[0];

function isRelaySetValue(value: unknown): value is RelaySetValue {
	if (Array.isArray(value)) {
		// Relay scalar arrays can contain nulls, but undefined elements are not valid setValue payloads.
		return value.every((item) => item !== undefined && isRelaySetValue(item));
	}

	return (
		value === null ||
		typeof value === 'string' ||
		typeof value === 'number' ||
		typeof value === 'boolean'
	);
}
/* eslint-enable @atlassian/perf-linting/no-quadratic-complexity */
